import { Alert, Box, Button, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconCar, IconCheck, IconMail, IconPhone, IconRotate } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { sporocilo } from '../api/napaka';
import { api, podatki } from '../api/odjemalec';
import { casIzMs } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { Oznaka } from '../strani/PregledRacunov';
import { telefonUrl } from '../ponudba/javno';
import { stanjePovprasevanja } from './povprasevanja';
import { SlikaVozila } from '../vozila/SlikaVozila';

/**
 * Podrobnosti povpraševanja (sem vodi tudi push obvestilo): kdo, kontakt z velikima gumboma za klic in e-pošto,
 * sporočilo, vozilo; ob odprtju novo postane prebrano. »Označi kot rešeno« ga zapre (lahko se spet odpre).
 */
export function PodrobnostiPovprasevanja() {
  const id = Number(useParams().id);
  const odjemalec = useQueryClient();
  const poizvedba = useQuery({
    queryKey: ['povprasevanje', id],
    queryFn: ({ signal }) => podatki(api.GET('/api/povprasevanja/{id}', { signal, params: { path: { id } } })),
  });
  const [dela, setDela] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const oznaceno = useRef(false);

  const osvezi = async () => {
    await odjemalec.invalidateQueries({ queryKey: ['povprasevanja'] });
    await odjemalec.invalidateQueries({ queryKey: ['povprasevanja-stevilo'] });
    await odjemalec.invalidateQueries({ queryKey: ['vozila'] });
    // pregled poslovanja (nova povpraševanja; skrita kartica se ob neogledanih pokaže)
    await odjemalec.invalidateQueries({ queryKey: ['zacetna-pregled'] });
  };

  // novo povpraševanje je s tem, ko ga uporabnik odpre, prebrano
  const p = poizvedba.data;
  useEffect(() => {
    if (p?.stanje === 'NOVO' && !oznaceno.current) {
      oznaceno.current = true;
      void podatki(api.POST('/api/povprasevanja/{id}/prebrano', { params: { path: { id } } }))
        .then((n) => odjemalec.setQueryData(['povprasevanje', id], n))
        .then(osvezi)
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- enkrat ob prvem prikazu novega
  }, [p?.stanje, id]);

  if (!p) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Povpraševanja ni bilo mogoče naložiti"
      />
    );
  }

  const reseno = async (vrednost: boolean) => {
    setNapaka(null);
    setDela(true);
    try {
      const n = await podatki(
        api.POST('/api/povprasevanja/{id}/reseno', { params: { path: { id } }, body: { vrednost } }),
      );
      odjemalec.setQueryData(['povprasevanje', id], n);
      await osvezi();
      notifications.show({
        color: 'green',
        message: vrednost ? 'Povpraševanje je rešeno.' : 'Povpraševanje je spet odprto.',
      });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(false);
    }
  };

  const s = stanjePovprasevanja(p.stanje);
  const zadeva = `Povpraševanje${p.voziloNaziv ? `: ${p.voziloNaziv}` : ''}`;
  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/povprasevanja"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Povpraševanja
      </Button>
      <GlavaZaslona
        naslov={p.ime ?? 'Povpraševanje'}
        opis={`Prejeto ${casIzMs(p.ustvarjeno)}`}
        dejanja={
          p.stanje === 'RESENO' ? (
            <Button
              variant="default"
              leftSection={<IconRotate size={18} />}
              onClick={() => void reseno(false)}
              loading={dela}
            >
              Ponovno odpri
            </Button>
          ) : (
            <Button leftSection={<IconCheck size={18} />} onClick={() => void reseno(true)} loading={dela}>
              Označi kot rešeno
            </Button>
          )
        }
      />
      <Group gap={6} mb="sm" mt={-8}>
        <Oznaka barva={s.barva}>{s.naziv}</Oznaka>
      </Group>
      {napaka && (
        <Alert color="red" variant="light" mb="md" role="alert">
          {napaka}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Stack gap="md">
          <Kartica naslov="Kontakt">
            <Stack gap="sm">
              {p.telefon && (
                <Button component="a" href={telefonUrl(p.telefon)} size="lg" leftSection={<IconPhone size={20} />}>
                  Pokliči {p.telefon}
                </Button>
              )}
              {p.email && (
                <Button
                  component="a"
                  href={`mailto:${p.email}?subject=${encodeURIComponent(zadeva)}`}
                  size="lg"
                  variant="light"
                  leftSection={<IconMail size={20} />}
                  styles={{ label: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                >
                  Piši na {p.email}
                </Button>
              )}
            </Stack>
          </Kartica>
          <Kartica naslov="Sporočilo">
            <Text style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{p.sporocilo}</Text>
          </Kartica>
        </Stack>
        <Stack gap="md">
          <Kartica naslov="Vozilo">
            {p.voziloId ? (
              <Paper
                component={Link}
                to={`/vozila/${p.voziloId}`}
                withBorder
                radius="md"
                p="xs"
                style={{ display: 'flex', gap: 12, alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
              >
                <SlikaVozila voziloId={p.voziloId} slikaId={p.slikaId} sirina={96} />
                <Box style={{ minWidth: 0 }}>
                  <Text fw={600}>{p.voziloNaziv}</Text>
                  <Text size="sm" c="dimmed">
                    Odpri vozilo
                  </Text>
                </Box>
              </Paper>
            ) : (
              <Group gap="xs" c="dimmed">
                <IconCar size={18} />
                <Text size="sm">{p.voziloNaziv ?? 'Splošno povpraševanje s strani zaloge.'}</Text>
              </Group>
            )}
          </Kartica>
          <Kartica naslov="Zgodovina">
            <Podatki
              vrstice={[
                ['Prejeto', casIzMs(p.ustvarjeno)],
                ['Prebrano', p.prebrano ? `${casIzMs(p.prebrano)}${p.prebral ? ` (${p.prebral})` : ''}` : ''],
                ['Rešeno', p.reseno ? `${casIzMs(p.reseno)}${p.resil ? ` (${p.resil})` : ''}` : ''],
              ]}
            />
          </Kartica>
        </Stack>
      </SimpleGrid>
    </ZaslonSeznama>
  );
}
